import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle2, ArrowRight, ChevronLeft, ChevronRight, UserPlus, RotateCcw, Phone, Video } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

/** returns "YYYY-MM-DD" for a Date's local calendar day */
const localDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

export default function Book() {
  const { branding } = useBranding();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [slots, setSlots] = useState([]);
  const [clientType, setClientType] = useState(""); // "" | "new" | "returning"
  const [meetingType, setMeetingType] = useState("phone"); // "phone" | "video"
  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD"
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", service_type: "", notes: "" });

  useEffect(() => {
    api.get("/appointments/config").then(r => setConfig(r.data)).catch(() => {});
    api.get("/appointments/slots?days_ahead=365").then(r => setSlots(r.data)).catch(() => {});
  }, []);

  // Filter slots by client type duration (30 for returning, 60 for new).
  const wantedDuration = clientType === "returning" ? 30 : clientType === "new" ? 60 : null;
  const filteredSlots = useMemo(() => {
    if (!wantedDuration) return [];
    return slots.filter(s => s.duration_min === wantedDuration);
  }, [slots, wantedDuration]);

  // Group by local date key
  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of filteredSlots) {
      const key = localDayKey(new Date(s.start_at));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return map;
  }, [filteredSlots]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) { toast.error("Pick a time first"); return; }
    if (!form.service_type) { toast.error("Choose the service type"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/appointments/book", { ...form, client_type: clientType, meeting_type: meetingType, slot_id: picked.id });
      setConfirmed({ ...r.data, service_type: form.service_type, first_name: form.first_name, meeting_type: meetingType });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
      api.get("/appointments/slots?days_ahead=365").then(r => setSlots(r.data));
      setPicked(null);
    } finally { setSubmitting(false); }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-background paper-texture grid place-items-center p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-md p-10 text-center" data-testid="book-success">
          <CheckCircle2 size={56} className="mx-auto text-primary mb-4" />
          <div className="label-caps mb-2">Booked</div>
          <h1 className="font-serif text-4xl mb-3">You're on the calendar, {confirmed.first_name}.</h1>
          <div className="rounded-md border border-primary/40 bg-primary/5 p-4 mb-6 text-left">
            <div className="text-sm space-y-1">
              <div className="flex items-center gap-2"><Calendar size={14} className="text-primary" /> {fmtDate(confirmed.start_at)}</div>
              <div className="flex items-center gap-2"><Clock size={14} className="text-primary" /> {fmtTime(confirmed.start_at)} · {confirmed.duration_min} min</div>
              <div className="flex items-center gap-2">
                {confirmed.meeting_type === "video" ? <Video size={14} className="text-primary" /> : <Phone size={14} className="text-primary" />}
                {confirmed.meeting_type === "video" ? "Video call" : "Phone call"}
              </div>
            </div>
            {confirmed.meeting_type === "video" && confirmed.video_link && (
              <a
                href={confirmed.video_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium"
                data-testid="book-success-video-link"
              >
                <Video size={14} /> Join video call
              </a>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            Check your email — we just sent a confirmation with a calendar invite (.ics) you can tap to add this to your phone or laptop calendar. Reply to that email if you need to reschedule.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/")} className="flex-1">Home</Button>
            <Button onClick={() => nav("/intake")} className="flex-1" data-testid="book-next-intake">Start intake now <ArrowRight size={12} className="ml-1" /></Button>
          </div>
        </div>
      </div>
    );
  }

  const daySlots = selectedDay ? (slotsByDay.get(selectedDay) || []) : [];
  const canFlipBack = monthCursor > new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const canFlipForward = monthCursor < new Date(new Date().getFullYear() + 1, new Date().getMonth(), 1);

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="label-caps hidden sm:block">Book an appointment</div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto p-6 lg:p-12 space-y-8">
        <div>
          <div className="label-caps mb-2">Appointments</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-3">Grab a time with a preparer.</h1>
          <p className="text-muted-foreground max-w-xl">
            Business hours are Sunday – Friday, 9 AM – 6 PM. Saturdays by appointment only.
          </p>
        </div>

        {/* Step 1 — client type */}
        <Card className="p-6" data-testid="book-client-type">
          <div className="label-caps text-[10px] mb-3">1. Are you a new or returning client?</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <ClientTypeCard
              active={clientType === "returning"}
              onClick={() => { setClientType("returning"); setSelectedDay(null); setPicked(null); }}
              icon={<RotateCcw size={16} />}
              label="Returning client"
              duration="30-minute slot"
              testid="book-type-returning"
            />
            <ClientTypeCard
              active={clientType === "new"}
              onClick={() => { setClientType("new"); setSelectedDay(null); setPicked(null); }}
              icon={<UserPlus size={16} />}
              label="New client"
              duration="1-hour slot"
              testid="book-type-new"
            />
          </div>
        </Card>

        {/* Step 1b — meeting type */}
        {clientType && (
          <Card className="p-6" data-testid="book-meeting-type">
            <div className="label-caps text-[10px] mb-3">1b. How would you like to meet?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <ClientTypeCard
                active={meetingType === "phone"}
                onClick={() => setMeetingType("phone")}
                icon={<Phone size={16} />}
                label="Phone call"
                duration="We call you at your number"
                testid="book-meeting-phone"
              />
              <ClientTypeCard
                active={meetingType === "video"}
                onClick={() => setMeetingType("video")}
                icon={<Video size={16} />}
                label="Video call"
                duration="Auto-generated video link — no app needed"
                testid="book-meeting-video"
              />
            </div>
          </Card>
        )}

        {/* Step 2 — month calendar */}
        {clientType && (
          <Card className="p-6" data-testid="book-calendar">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps text-[10px] mb-1">2. Pick a date</div>
                <div className="font-serif text-2xl" data-testid="book-month-title">
                  {MONTHS[monthCursor.getMonth()]} {monthCursor.getFullYear()}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm" variant="outline" disabled={!canFlipBack}
                  onClick={() => { const d = new Date(monthCursor); d.setMonth(d.getMonth() - 1); setMonthCursor(d); setSelectedDay(null); setPicked(null); }}
                  aria-label="Previous month" data-testid="book-month-prev"
                >
                  <ChevronLeft size={14} />
                </Button>
                <Button
                  size="sm" variant="outline" disabled={!canFlipForward}
                  onClick={() => { const d = new Date(monthCursor); d.setMonth(d.getMonth() + 1); setMonthCursor(d); setSelectedDay(null); setPicked(null); }}
                  aria-label="Next month" data-testid="book-month-next"
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>

            <MonthGrid
              monthCursor={monthCursor}
              slotsByDay={slotsByDay}
              selectedDay={selectedDay}
              onPick={(key) => { setSelectedDay(key); setPicked(null); }}
            />

            {/* Slots for selected day */}
            {selectedDay && (
              <div className="mt-6 pt-6 border-t border-border" data-testid="book-day-slots">
                <div className="label-caps text-[10px] mb-3">
                  {new Date(selectedDay + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </div>
                {daySlots.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No open slots on this day. Pick another highlighted day.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {daySlots.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setPicked(s)}
                        data-testid={`book-slot-${s.id}`}
                        className={`text-xs px-3 py-2 rounded-md border transition-colors ${
                          picked?.id === s.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:border-primary"
                        }`}
                      >
                        {fmtTime(s.start_at)} · {s.duration_min}m
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Step 3 — details */}
        {picked && (
          <Card className="p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="book-form">
              <div className="label-caps text-[10px] mb-2">
                3. Your details · {new Date(picked.start_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} at {fmtTime(picked.start_at)} · {picked.duration_min} min
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="First name" required><Input required value={form.first_name} onChange={e => setF("first_name", e.target.value)} data-testid="book-first-name" /></Field>
                <Field label="Last name" required><Input required value={form.last_name} onChange={e => setF("last_name", e.target.value)} data-testid="book-last-name" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Email" required><Input required type="email" value={form.email} onChange={e => setF("email", e.target.value)} data-testid="book-email" /></Field>
                <Field label="Phone"><Input type="tel" value={form.phone} onChange={e => setF("phone", e.target.value)} data-testid="book-phone" /></Field>
              </div>
              <Field label="What kind of return?" required>
                <select
                  required
                  value={form.service_type}
                  onChange={e => setF("service_type", e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="book-service-type"
                >
                  <option value="">Choose one…</option>
                  {Object.entries(config?.service_types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Anything we should know?">
                <Textarea rows={3} value={form.notes} onChange={e => setF("notes", e.target.value)} placeholder="Optional — first-time filer, complex situation, etc." data-testid="book-notes" />
              </Field>
              <div className="flex justify-between gap-3 pt-3 border-t border-border">
                <Button type="button" variant="ghost" onClick={() => setPicked(null)}>Change time</Button>
                <Button type="submit" size="lg" disabled={submitting} data-testid="book-submit-btn">
                  {submitting ? "Booking…" : "Confirm appointment"}
                </Button>
              </div>
            </form>
          </Card>
        )}

        <PoweredBy className="text-center mt-6" />
      </section>
    </div>
  );
}

const ClientTypeCard = ({ active, onClick, icon, label, duration, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-4 rounded-md border transition-colors ${
      active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"
    }`}
  >
    <div className="flex items-center gap-2 mb-1">{icon}<span className="text-sm font-medium">{label}</span></div>
    <div className={`text-xs ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{duration}</div>
  </button>
);

const MonthGrid = ({ monthCursor, slotsByDay, selectedDay, onPick }) => {
  const year = monthCursor.getFullYear();
  const month = monthCursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);

  // Build cells: prefix blanks + days
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Pad trailing to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div className="grid grid-cols-7 mb-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-[10px] uppercase tracking-widest text-muted-foreground text-center py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="book-month-grid">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} className="aspect-square" />;
          const cellDate = new Date(year, month, d);
          const key = localDayKey(cellDate);
          const count = (slotsByDay.get(key) || []).length;
          const isPast = cellDate < today;
          const isToday = key === localDayKey(today);
          const isSelected = key === selectedDay;
          const hasSlots = count > 0;
          const disabled = isPast || !hasSlots;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onPick(key)}
              data-testid={`book-day-${key}`}
              className={[
                "aspect-square rounded-md border text-sm flex flex-col items-center justify-center relative transition-colors",
                isSelected
                  ? "bg-primary text-primary-foreground border-primary"
                  : hasSlots
                    ? "bg-background border-border hover:border-primary cursor-pointer"
                    : "bg-secondary/30 border-transparent text-muted-foreground/50 cursor-not-allowed",
                isToday && !isSelected ? "ring-1 ring-accent" : "",
              ].join(" ")}
            >
              <span className={`font-medium ${isToday && !isSelected ? "text-accent" : ""}`}>{d}</span>
              {hasSlots && (
                <span className={`text-[9px] mt-0.5 ${isSelected ? "text-primary-foreground/80" : "text-primary"}`}>
                  {count} open
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-background border border-border" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-primary" /> Selected</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-secondary/60" /> No slots</span>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="label-caps text-[10px]">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
    {children}
  </div>
);
